import React from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Forge shared 3-pane shell.
 * - Left: control panel (objective/audience/tone/etc.)
 * - Center: workspace (inputs + outputs)
 * - Right: contextual sidebar (drafts, brand voice, tips)
 *
 * Mobile (<lg): stacks vertically — controls on top, workspace, then context.
 */
export default function ForgeShell({ left, center, right, testid }) {
  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-[1600px] mx-auto" data-testid={testid}>
      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)_320px] gap-5">
        <aside className="lg:sticky lg:top-[88px] lg:self-start surface p-5 space-y-5 max-h-[calc(100vh-100px)] lg:overflow-y-auto scrollbar-thin" data-testid="forge-left">
          {left}
        </aside>
        <main className="min-w-0 space-y-5" data-testid="forge-main">
          {center}
        </main>
        <aside className="lg:sticky lg:top-[88px] lg:self-start surface p-5 space-y-5 max-h-[calc(100vh-100px)] lg:overflow-y-auto scrollbar-thin" data-testid="forge-right">
          {right}
        </aside>
      </div>
    </div>
  );
}

// ---------- Reusable control primitives ----------

export function ControlLabel({ children, hint }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-meta font-bold">{children}</p>
      {hint && <p className="text-[11px] text-meta-soft mt-0.5">{hint}</p>}
    </div>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-full bg-zinc-100 p-1 flex-wrap max-w-full overflow-x-auto scrollbar-thin gap-y-1" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const isActive = v === value;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition focus-ring whitespace-nowrap ${
              isActive ? "ashra-gradient-bg text-white shadow-sm" : "text-meta hover:text-ink"
            }`}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function Chips({ options, value, onChange, multi = false, testid }) {
  const selected = multi ? new Set(value || []) : value;
  const toggle = (v) => {
    if (multi) {
      const s = new Set(selected);
      s.has(v) ? s.delete(v) : s.add(v);
      onChange(Array.from(s));
    } else {
      onChange(v);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const isActive = multi ? selected.has(v) : selected === v;
        return (
          <button key={v} type="button" onClick={() => toggle(v)} data-testid={`${testid}-${v}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition focus-ring ${
              isActive ? "border-violet-400 bg-ai-soft text-ai" : "border-line bg-white text-meta hover:border-zinc-300 hover:text-ink"
            }`}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function Textarea({ value, onChange, placeholder, rows = 3, testid }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition resize-none placeholder:text-meta-soft"
    />
  );
}

export function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition">
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        return <option key={v} value={v}>{label}</option>;
      })}
    </select>
  );
}

// ---------- Output primitives ----------

export function OutputCard({ title, children, action }) {
  return (
    <div className="surface p-5">
      {(title || action) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          {title && <h3 className="font-display text-sm font-bold text-ink uppercase tracking-wider">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function CopyableText({ text, testid }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="group flex items-start gap-2 rounded-xl bg-zinc-50 border border-line p-3" data-testid={testid}>
      <p className="flex-1 min-w-0 text-sm text-ink whitespace-pre-wrap break-words">{text || "—"}</p>
      <button onClick={copy} className="opacity-50 hover:opacity-100 transition text-meta p-1 -m-1" data-testid={testid ? `${testid}-copy` : undefined}>
        {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

export function EmptyState({ title, body }) {
  return (
    <div className="surface p-10 text-center" data-testid="forge-empty-state">
      <div className="mx-auto h-10 w-10 rounded-2xl bg-ai-soft text-ai grid place-items-center mb-3">
        <span className="text-base font-bold">✨</span>
      </div>
      <p className="font-display text-base font-bold text-ink">{title}</p>
      {body && <p className="text-xs text-meta mt-1 max-w-md mx-auto">{body}</p>}
    </div>
  );
}
